import { useContext } from "react";
import { useRouter } from "next/router";
import PgSidebar from "../ui/pg-sidebar";
import PgNavMenuItems from "../ui/pg-nav-menu-items";
import BusinessContext from "../../contexts/business-context";
import UserContext from "../../contexts/user-context";
import { setCachedBusiness } from "../../lib/utils";
import { handlelogOut } from "../../lib/auth";

const HELP_CENTER_URL = "https://docs.google.com/forms/d/e/1FAIpQLScIaAP1FSbZuFGwPsxp4ojs8IkK0IT29JsAdOtrKAlpiaMQhg/viewform";
const REPORT_BUG_URL = "https://forms.gle/rqLWcSkrpiNouynD8";

/**
 * Wires the pegment `PgSidebar` up to this app's real data — business/user
 * context, routing, auth. Rendered by `Shell` in place of the legacy
 * `SideBar` + `MainMenuItems` + `NavMenuItems` + `BottomNav` stack; see
 * pegment.md for the generic, context-free pieces this composes.
 *
 * Known gap: the legacy sidebar had a mobile off-canvas drawer + backdrop
 * (see `app/components/layout/sidebar.js`). pegment.css's `.pg-sidebar`
 * only defines the desktop icon-rail collapse (`.is-collapsed`) — there's
 * no off-canvas/backdrop treatment yet, so on small screens this renders
 * as a normal (non-hidden) sidebar until that's built separately.
 */
const AppSidebar = () => {
    const router = useRouter();
    const { user } = useContext(UserContext);
    const { activeBusiness: business, businessList, isDeveloperRegistered } = useContext(BusinessContext);

    if (!user || !business) return null;

    const isOnboarding = user.is_pseudonymous
        || business.is_pseudonymous
        || router.asPath === `/${business.uuid}/onboarding`;

    const topNav = isOnboarding
        ? [{ id: "onboard", label: "Get Started", icon: "power", href: `/${business.uuid}/onboarding` }]
        : [
            { id: "search", label: "Search", icon: "search" },
            { id: "workpeg-ai", label: "Workpeg AI", icon: "sparkles", href: `/${business.uuid}/chats` },
            { id: "dashboard", label: "Dashboard", icon: "home", href: `/${business.uuid}/dashboard` },
        ];

    const bottomNav = isDeveloperRegistered === false ? [] : [
        ...(!user.is_pseudonymous && !business.is_pseudonymous
            ? [{ id: "settings", label: "Settings", icon: "settings-2", href: `/${business.uuid}/manage-people` }]
            : []),
        { id: "report-bug", label: "Report a Bug", icon: "bug", href: REPORT_BUG_URL, target: "_blank" },
        { id: "help", label: "Help & Support", icon: "lifebuoy", href: HELP_CENTER_URL, target: "_blank" },
    ];

    const pegsNavItem = { id: "pegs", label: "Pegs", icon: "layout-grid", href: `/${business.uuid}/pegs` };

    const activeId = [...topNav, ...bottomNav, pegsNavItem]
        .find((item) => item.href && item.href === router.asPath)?.id;

    const businesses = (businessList || []).map((b) => ({
        id: b.uuid,
        name: b.name,
        plan: "Free Plan",
        active: b.uuid === business.uuid,
    }));

    const handleBusinessChange = (id) => {
        setCachedBusiness(id);
        window.location.replace(`/${id}/dashboard`);
    };

    const handleDropdownAction = (action) => {
        if (action === "add") router.push("/businesses/new");
        if (action === "settings") router.push(`/${business.uuid}/manage-people`);
        if (action === "logout") handlelogOut(router);
    };

    const handleHelpAction = (item) => {
        window.open(item === "report-bug" ? REPORT_BUG_URL : HELP_CENTER_URL, "_blank");
    };

    return (
        <PgSidebar
            logoWord="Workpeg"
            topNav={topNav}
            bottomNav={bottomNav}
            active={activeId}
            user={{ name: user.first_name, email: user.email, company: business.name }}
            businesses={businesses}
            onBusinessChange={handleBusinessChange}
            onDropdownAction={handleDropdownAction}
            onHelpAction={handleHelpAction}
        >
            {!isOnboarding && (
                <PgNavMenuItems sections={[{ items: [pegsNavItem] }]} activeId={activeId} />
            )}
        </PgSidebar>
    );
};

export default AppSidebar;
